import { Editor } from '@tinymce/tinymce-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';

import ColorPickerPanel from '../../../../components/ColorPicker/ColorPickerPanel';
import EmojiPickerPanel from '../../../../components/Emoji/EmojiPickerPanel';
import useAlert from '../../../../hooks/useAlert';
import { RootState } from '../../../../modules';
import { editContent } from '../../../../modules/editor';
import { RGBToHex } from '../../../../util/color';
import { copyObject } from '../../../../util/object';
import { stringCss } from '../../../../util/parseStyle';
import { hasHttp, isUrl, isValidLink } from '../../../../util/validator';

import iconTextAlignCenter from '../../../../assets/images/icon/toolbar/align-center.svg';
import iconTextAlignJustify from '../../../../assets/images/icon/toolbar/align-justify.svg';
import iconTextAlignLeft from '../../../../assets/images/icon/toolbar/align-left.svg';
import iconTextAlignRight from '../../../../assets/images/icon/toolbar/align-right.svg';
import MergeButton from '../../../../components/MergeButton';
import useDebounce from '../../../../hooks/useDebounce';
import { removeInvalidStyleAndAttr } from '../../../../util/dom';
import { addBrLine, cleanUpHtml } from '../../../../util/html';
import { isAllowedFieldForMailMerge } from '../../../../util/mailMerge';
import { addLinkToText, trimBlankString } from '../../../../util/string';
import { useHistoryMode } from '../../../History/HistoryContext';
import TextEditCover from '../TextEditCover';
import { toolbar } from './meta';
import useToolbarFloat from './useToolbarFloat';

const parser = new DOMParser();

type TextEditProps = {
	innerContent: any;
	contentField: string;
	style: any;
	boxStyle: any;
	contentId: number;
	contentIndex: number;
	subIndex?: number;
	markToolbar: (contentId: number, currentIndex: number, itemType: string) => void;
	markedToolbar: any;
	contentCategory?: string;
	textTemplate?: boolean;
};

function TextEdit({ innerContent, contentField, style, boxStyle, contentId, contentIndex, subIndex, markToolbar, markedToolbar, contentCategory, textTemplate }: TextEditProps) {
	const { isInHistory } = useHistoryMode();
	const dispatch = useDispatch();
	const Alert = useAlert();
	const editorRef = useRef<Editor>(null);
	const isAfterLocalSave = useRef(false);
	const [tinymce, setTinymce] = useState({} as any);
	const [debouncedEditedText, editedText, setEditedText] = useDebounce(addBrLine(innerContent[contentField]), 3000);
	const [showToolbar, setShowToolbar] = useState(false);
	const [showContent, setShowContent] = useState(true);
	const [listInfoFields, setListInfoFields] = useState([] as any);
	const [listVarsFields, setListVarsFields] = useState([] as any);
	const [foreColorPickerShow, setForeColorPickerShow] = useState(false);
	const [foreColorValue, setForeColorValue] = useState('');
	const [backColorPickerShow, setBackColorPickerShow] = useState(false);
	const [backColorValue, setBackColorValue] = useState('');
	const [emojiShow, setEmojiShow] = useState(false);
	const [colorPanelFixed, setColorPanelFixed] = useState(false);
	const [colorPanelFixedLeft, setColorPanelFixedLeft] = useState(0);
	const textAlignChanged = useRef<boolean>();

	const email = useSelector((state: RootState) => state.editor.email);
	const { listInfo, selectedContentId, prevSelectedContentId, container, listVars, systemFields } = useSelector((state: RootState) => state.editor);
	const { commonStyle } = container;
	const accountPlan = useSelector((state: RootState) => state.auth.account.billing.plan);
	const service = useSelector((state: RootState) => state.auth.service);
	const isNoLinkValidationAccount = useSelector((state: RootState) => state.auth.account.options.editorNoLinkValidation);
	const msg = useSelector((state: RootState) => state.editor.service.msg);

	const linkExcludes = ['permalink', 'footer'];

	const contentTarget = subIndex !== undefined ? `${contentId}/content/${contentIndex}/${subIndex}` : `${contentId}/content/${contentIndex}`;
	const styleTarget = `${contentId}/style/${contentIndex}`;

	const whitelist = useMemo(() => {
		const fields = systemFields?.systemMergeURLFields || [];
		return fields?.map(({ substitution }) => `$%${substitution}%$`);
	}, [systemFields]);

  useToolbarFloat(editorRef, {
    onTopIntersectOut: (left) => {
      setColorPanelFixed(false);
      setColorPanelFixedLeft(left);
    },
		onTopIntersectIn: (left) => {
      setColorPanelFixed(true);
      setColorPanelFixedLeft(left);
    },
		onBottomIntersectIn: (left) => {
      setColorPanelFixed(true);
      setColorPanelFixedLeft(left);
    },
		onBottomIntersectOut: () => {
			setColorPanelFixed(false);
		},
  })

	useEffect(() => {
		const initialText = addBrLine(innerContent[contentField]);
		if (initialText === debouncedEditedText) return;
		dispatch(editContent({ target: `${contentTarget}/text`, data: debouncedEditedText, mode: 'force' }));
		isAfterLocalSave.current = true;
	}, [debouncedEditedText]);

	useEffect(() => {			
		if (isInHistory) return;
		if (selectedContentId !== contentId) {
			isAfterLocalSave.current = false;
			init();
		}		
		if (prevSelectedContentId === contentId) {
			
			if (editedText.length === 0) {
				return;
			}
			dispatch(editContent({ target: `${contentTarget}/text`, data: editedText }));
			removeTempTextFromLocal();	
		}
	}, [selectedContentId]);

	useEffect(() => {
		if (isInHistory) return;
		if (editedText.length !== 0 && !contentCategory?.includes('shop')) {
			dispatch(editContent({ target: `${contentTarget}/text`, data: editedText }));
			removeTempTextFromLocal();
		}
		
		const toolbarIndex = subIndex !== undefined ? Number(`${contentIndex}${subIndex}`) : contentIndex;
		if (markedToolbar?.contentId !== contentId || markedToolbar?.currentIndex !== toolbarIndex || markedToolbar.itemType !== 'text') {
			isAfterLocalSave.current = false;
			init();
		} 
	}, [markedToolbar]);

	useEffect(() => {
		setListInfoFields([
			{ label: '회사/단체명', value: listInfo.companyName },
			{ label: '주소', value: listInfo.companyAddress },
			{ label: '연락처', value: listInfo.companyContact },
		]);
	}, [listInfo]);

	useEffect(() => {
		const filtedListVars = listVars.filter(isAllowedFieldForMailMerge);
		const filterdSystemFields = systemFields.systemMergeSubscriberFields
			.filter(field => field.substitution === "subscribed_date")
			.map(field => ({ ...field, label: "구독일" }));
		const updatedListVars = [
			...filtedListVars,
			...filterdSystemFields
		];
		
		setListVarsFields(updatedListVars);
	}, [listVars, systemFields]);

	useEffect(() => {
		
		setEditedText(addBrLine(innerContent[contentField]));
		init();
	}, [innerContent]);

	const init = () => {
		if (isAfterLocalSave.current) return;

		toggleForeColorPickerShow(false);
		toggleBackColorPickerShow(false);
		toggleEmojiPicker(false);

		if (!textTemplate) {
			setShowContent(true);
			setShowToolbar(false);
		}

		isAfterLocalSave.current = false;
	}

	useEffect(() => {
		if (textTemplate) {
			setShowToolbar(true);
		}
	}, [textTemplate])


	const startEditing = () => {
		const toolbarIndex = subIndex !== undefined ? Number(`${contentIndex}${subIndex}`) : contentIndex;
		markToolbar(contentId, toolbarIndex, 'text');

		setTimeout(() => {
			setShowToolbar(true);
		}, 10);

		setTimeout(() => {
			checkEditorToolbar();
		}, 500);
	}

	const checkEditorToolbar = () => {
		let fontFamilyButton = document?.querySelector('.tox-tbtn[title="서체"]');
		let fontSizeButton = document?.querySelector('.tox-tbtn[title="폰트 사이즈"]');
		fontFamilyButton?.setAttribute('style', `width: 85px`);
		fontSizeButton?.setAttribute('style', `width: 70px`);
	}

	const handleEditorChange = (innerHTML: string) => {
		setEditedText(addBrLine(tinymce.getContent()));
	}

	const detectSelection = (editor:any) => {
		toggleForeColorPickerShow(false);
		toggleBackColorPickerShow(false);

		if (editor.selection === null) {
			return;
		}

		const node = editor.selection.getNode();

		// color
		let color: string = editor.selection.dom.getStyle(node, 'color') || '';
		let bg: string = editor.selection.dom.getStyle(node, 'background-color') || '';

		if (color.length === 0) {
			color = RGBToHex(window?.getComputedStyle(node).getPropertyValue('color'));
		}
		if (bg.length === 0) {
			bg = RGBToHex(window?.getComputedStyle(node).getPropertyValue('background-color'));
		}

		let bgTarget: any = document?.querySelector('.tox-tbtn--select.back-color .tox-icon');
		let bgCurrentColor: any = document?.querySelector('.tox .tox-tbtn--select.back-color .back-current-color');

		if (bgCurrentColor === null) {
			let bgBar = document.createElement('div');
			bgBar.classList.add('back-current-color');
			bgBar.style.backgroundColor = bg;
			bgTarget.appendChild(bgBar);
		} else {
			bgCurrentColor.style.backgroundColor = bg;
		}

		let textTarget: any = document?.querySelector('.tox-tbtn--select.fore-color');
		let textCurrentColor: any = document?.querySelector('.tox .tox-tbtn--select.fore-color .text-current-color');

		if (textCurrentColor === null) {
			let colorBar = document.createElement('div');
			colorBar.classList.add('text-current-color');
			colorBar.style.backgroundColor = color;
			textTarget.appendChild(colorBar);
		} else {
			textCurrentColor.style.backgroundColor = color;
		}

		if (color.length !== 0) {
			setForeColorValue(color);
		}
		if (bg.length !== 0) {
			if (bg === 'transparent') {
				bg = '';
			}
			setBackColorValue(bg);
		}

		// align 
		let textAlignIconParent = document?.querySelector('.tox-tbtn.tox-tbtn--select.align > .tox-icon');
		let svgs = document?.querySelector('.tox-tbtn.tox-tbtn--select.align > .tox-icon svg');
		let imgs = document?.querySelector('.tox-tbtn.tox-tbtn--select.align > .tox-icon img');
		svgs?.remove();
		imgs?.remove();

		let n = document?.createElement('img');
		switch (getCurrentAlign(editor)) {
			case '가운데':
				n.setAttribute('src', iconTextAlignCenter);
				textAlignIconParent?.setAttribute('title', '가운데');
				break;
			case '왼쪽':
				n.setAttribute('src', iconTextAlignLeft);
				textAlignIconParent?.setAttribute('title', '왼쪽');
				break;
			case '오른쪽':
				n.setAttribute('src', iconTextAlignRight);
				textAlignIconParent?.setAttribute('title', '오른쪽');
				break;
			case '양쪽정렬':
				n.setAttribute('src', iconTextAlignJustify);
				textAlignIconParent?.setAttribute('title', '양쪽정렬');
				break;
			default:
				n.setAttribute('src', iconTextAlignLeft);
				break;
		}

		textAlignIconParent?.appendChild(n);
	}

	const customSetup = (editor: any) => {
		setTinymce(editor);

		editor.on('NodeChange', (e: any) => {
			if (textAlignChanged.current) {
				textAlignChanged.current = false;
				detectSelection(editor);
			}
			/* 
			# 불릿 컬러 보정
			LI 태그가 포함한 SPAN 태그가 1개일 경우에 LI에 유일한 SPAN의 스타일을 적용한다.
			*/
			let dom: any = parser.parseFromString(editor.getContent(), 'text/html');
			
			// Get depth and text of each leaf node
			// function getLeafNodeInfo(node: Node, currentDepth: number = 0): {depth: number, text: string}[] {
			// 	// If node has no children, it's a leaf node - return its depth and text
			// 	if (!node.childNodes.length) {
			// 		return [{
			// 			depth: currentDepth,
			// 			text: node.textContent || ''
			// 		}];
			// 	}
				
			// 	// Recursively get info of leaf nodes in children
			// 	let nodeInfo: {depth: number, text: string}[] = [];
			// 	for (let child of Array.from(node.childNodes)) {
			// 		nodeInfo = nodeInfo.concat(getLeafNodeInfo(child, currentDepth + 1));
			// 	}
			// 	return nodeInfo;
			// }
			
			// const leafNodeInfo = getLeafNodeInfo(dom);
			// console.log("Leaf node info:", leafNodeInfo);
			
			let liTagList = dom.querySelectorAll('li');

			for (let index = 0; index < Array.from(liTagList).length; index++) {
				let element = liTagList[index];
				let children = element.childNodes;
				if (Array.from(children).filter((item: any) => item.nodeName === 'SPAN').length === 1) {
					const { color, fontSize } = element.childNodes[0].style ?? {};
					try {
						//font-size 있을때만 font-size style 추가
						element.setAttribute('style', `${color != null ? `color: ${color}` : ''}; ${fontSize != null ? `font-size: ${fontSize};'` : ''}`);
					} catch (error) {
						console.error(error);
					}

					try {
						if (element?.childNodes[0]?.nodeName === '#text') {
							element.setAttribute('style', '');
						}
					} catch (error) {
						console.error(error);
					}
				}
			}

			saveTempTextToLocal(addBrLine(dom?.documentElement?.querySelector('body')?.innerHTML));

			setEditedText(addBrLine(dom?.documentElement?.querySelector('body')?.innerHTML));
		});

		editor.on('keyup', (e: any) => {
			if (editor.getContent()?.length === 0) {
				editor.setContent('&nbsp;&nbsp;')
			}
		});

		editor.on('ExecCommand', (e: any) => {
			const { command, value } = e;
			if (command === 'FontSize') {
				var node = editor.selection.getNode();

				if (node) {
					let childNodes = node.getElementsByTagName('li');
					if (childNodes) {
						for (let index = 0; index < childNodes.length; index++) {
							const element = childNodes[index];
							element.setAttribute('style', `font-size: ${value}`);
						}
					}
				}
				//toolbar link
			} else if (command === 'mceInsertLink') {
				let node = editor.selection.getNode();

				let childNodes = node.getElementsByTagName('a');
				if (childNodes) {
					for (let index = 0; index < childNodes.length; index++) {
						const element = childNodes[index];
						element.setAttribute('style', stringCss(commonStyle.link, true));
					}
				}
				//auto link
			} else if (command === 'createlink') {
				let node = editor.selection.getNode();
				let isMailtoLink = node.getAttribute('href')?.indexOf('mailto:') > -1

				if (isMailtoLink) {
					node.setAttribute('class', 'stb-mailto')
					node.setAttribute('style', stringCss(commonStyle.text, true))
				} else {
					node.setAttribute('style', stringCss(commonStyle.link, true))
				}
			}

		});

		// 커서 위치 변경
		setTimeout(() => {
			if (editor === null) {
				return;
			}
			editor.selection.select(editor.getBody(), true);
			editor.selection.collapse(false);
		}, 300);

		// 에디터 활성 직후 detect 
		setTimeout(() => {
			detectSelection(editor);
		}, 300);

		editor.on('click', (e: any) => {
			detectSelection(editor);
		})

		editor.on('keyup', (e: any) => {
			detectSelection(editor);
		})

		editor.on('submit', (e: any) => {
			e.preventDefault();
			return false;
		});

		editor.on('OpenWindow', (e: any) => {
			window.parent.postMessage({ msg: 'dialogShow' }, '*');

			// 링크 윈도우 예외처리
			const validateLinkInput = (v: string) => {
				let value = trimBlankString(v);

				if (value.length === 0) {
					return { isValid: false, value: null, message: Alert?.msg?.REQUIRE_URL };
				}

				if (isNoLinkValidationAccount) {
					return { isValid: true, value };
				}

				if (!isValidLink(value, whitelist)) {
					return { isValid: false, value: null, message: Alert?.msg?.CHECK_URL_TYPE };
				}

				if (isUrl(value) && !hasHttp(value)) {
					value = `http://${value}`;
				}
				return { isValid: true, value };
			}

			const submit = (e: Event) => {
				clearError();
				const a: any = document.querySelector('.tox-control-wrap input');
				const { isValid, value, message } = validateLinkInput(a.value);

				if (!isValid) {
					e.stopPropagation();
					Alert.error(Alert?.msg?.DEFAULT_ERROR);
					setError(message || Alert?.msg?.DEFAULT_ERROR);
					return;
				}

				a.value = value;
			}

			const setError = (text: string) => {
				const msg: HTMLDivElement | null = document.querySelector('div.stb-link-error');
				if (!msg) return;
				msg.innerText = text;
				msg.classList.add('show');
			}

			const clearError = () => {
				const msg: HTMLDivElement | null = document.querySelector('div.stb-link-error');
				if (!msg) return;
				msg.innerText = '';
				msg.classList.remove('show');
			}

			const linkTagElement: HTMLDivElement = document.createElement('div');

			const showLinkTag = (e: any) => {
				linkTagElement.classList.add('link-form-dropdown');
				linkTagElement.innerHTML = `
				<button data-value="$%unsubscribe%$"><strong>수신거부</strong><em>$%unsubscribe%$</em></button><button data-value="$%permalink%$"><strong>웹에서 보기</strong><em>$%permalink%$</em></button>
				`;
				const btns = Array.from(linkTagElement.querySelectorAll('button'));
				const inputTarget = e.target;
				for (const btn of btns) {
					btn.addEventListener('click', (e: any) => {
						inputTarget.value = btn.dataset.value
						removeLinkTag(linkTagElement);
					})
				}
				e.target.parentNode.insertBefore(linkTagElement, e.target.nextSibling);
			}

			const removeLinkTag = (element: any) => {
				element.remove();
			}

			if (e.dialog.getData().hasOwnProperty('url')) {
				const dialog = document.querySelector('.tox-dialog');
				dialog?.classList?.add('link-dialog');

				const headerTitle: HTMLDivElement | null = document.querySelector('.tox-dialog .tox-dialog__header .tox-dialog__title');
				if (headerTitle) {
					headerTitle.innerHTML = 'URL을 입력하세요';
				}

				const wrapper = document.querySelector('.tox-control-wrap');
				if (wrapper) {
					const msg = document.createElement('div');
					msg.classList.add('stb-link-error');
					wrapper.parentElement?.appendChild(msg);

					const containter = document.createElement('div');
					wrapper.appendChild(containter);

					const onSelect = (value: string) => {
						const input = wrapper.querySelector('input');
						if (!input) return;
						input.value += value;
					}

					const onClose = () => dialog?.focus();

					ReactDOM.render(<MergeButton mode="inputright" accountPlan={accountPlan} listVars={listVars} systemFields={systemFields?.systemMergeURLFields || []} onSelect={onSelect} onClose={onClose}></MergeButton>, containter);
				}
				document.querySelector('.tox-dialog__footer-end > button:nth-child(2)')?.addEventListener('click', (e: any) => {
					submit(e);
				});
				document.querySelector('.tox-control-wrap input')?.addEventListener('keydown', (e: any) => {
					if (e.key === 'Enter') {
						submit(e);
					}
				});
				document.querySelector('.tox-control-wrap input')?.addEventListener('keyup', (e: any) => {
					if (e.target.value === '$') {
						if (document.querySelector('.link-form-dropdown') !== null) {
							return;
						}
						showLinkTag(e);
					} else {
						removeLinkTag(linkTagElement);
					}
				});
				document.querySelector('.tox-dialog-wrap')?.addEventListener('click', (e: any) => {
					removeLinkTag(linkTagElement);
				})
			}
		});

		editor.on('CloseWindow', (e: any) => {
			window.parent.postMessage({ msg: 'dialogHide' }, '*');
		});

		editor.ui.registry.addButton('stbUnlink', {
			icon: 'unlink',
			onAction: function () {
				Alert.confirm(Alert?.msg?.UNLINK_TEXT, () => {
					editor.execCommand('Unlink');
					setTimeout(() => {
						const node = editor.selection.getNode();
						if (RGBToHex(node.style.color) === RGBToHex(commonStyle.link.color)) {
							editor.execCommand('removeformat');
						}
						setEditedText(addBrLine(editor.getContent()));
					}, 10);
				})
			}
		});

		editor.ui.registry.addButton('stbForeColorPicker', {
			icon: 'text-color',
			text: 'stb-fore-color-picker',
			onSetup: function (buttonApi: any) {
				let a = Array.from(document.querySelectorAll(`#texteditor${contentId}${contentIndex} ~ .tox span`)).find(el => el.textContent === 'stb-fore-color-picker');

				a?.classList.add('blind');
				a?.parentElement?.classList.add('fore-color');
			},
			onAction: function () {
				toggleForeColorPickerShow(true);
			}
		});

		editor.ui.registry.addButton('stbBackColorPicker', {
			icon: 'highlight-bg-color',
			text: 'stb-back-color-picker',
			onSetup: function (buttonApi: any) {
				let a = Array.from(document.querySelectorAll(`#texteditor${contentId}${contentIndex} ~ .tox span`)).find(el => el.textContent === 'stb-back-color-picker');
				a?.classList.add('blind');
				a?.parentElement?.classList.add('back-color');
			},
			onAction: function () {
				toggleBackColorPickerShow(true);
			}
		});

		editor.ui.registry.addButton('stbEmoji', {
			icon: 'emoji',
			text: 'stb-emoji-picker',
			onSetup: function (buttonApi: any) {
				let a = Array.from(document.querySelectorAll(`#texteditor${contentId}${contentIndex} ~ .tox span`)).find(el => el.textContent === 'stb-emoji-picker');
				a?.classList.add('blind');
				a?.parentElement?.classList.add('emoji');
			},
			onAction: function () {
				toggleEmojiPicker(true);
			}
		});

		editor.ui.registry.addMenuButton('mergeTagButton', {
			icon: 'merge',
			text: 'stb-merge',
			onSetup: function (buttonApi) {
				let a = Array.from(document.querySelectorAll(`#texteditor${contentId}${contentIndex} ~ .tox span`)).find(el => el.textContent === 'stb-merge');
				a?.classList.add('blind');
				a?.parentElement?.classList.add('merge');

				a?.parentElement?.addEventListener('click', (e: any) => {
					if (!service.isStandard) {
						const target = e.currentTarget;
						setTimeout(() => {
							if (!target.classList.contains('tox-tbtn--active')) return;
							target.click(); // 버튼 비활성 시키기 위해 한 번 더 클릭
						}, 500)
						return;
					}

					setTimeout(() => {
						let mergeTagLabels: any = document.querySelectorAll('.tox-menu .tox-collection__item-label');
						let mergeTagLabelsList: any = Array.from(mergeTagLabels);
						mergeTagLabelsList.forEach((item: any) => {
							item.innerHTML = item.innerText.split('|').map((value, i) => i === 1 ? `<small>${value}</small>` : value).join('');
						});
					}, 300);
					setTimeout(() => {
						let mergeTagLabels: any = document.querySelectorAll('.tox-menu .tox-collection__item');
						let mergeTagLabelsList: any = Array.from(mergeTagLabels);
						mergeTagLabelsList.forEach((item: any) => {
							item.classList.add('ready');
						});
					}, 350);
				});
			},
			fetch: function (callback: any) {
				const a = Array.from(document.querySelectorAll(`#texteditor${contentId}${contentIndex} ~ .tox span`)).find(el => el.textContent === 'stb-merge');
				const isClicked = a?.parentElement?.classList.contains('tox-tbtn--active');
				if (!service.isStandard) {
					if (!isClicked) return;
					window.parent.postMessage({ msg: 'openUpgradeGuideModal', guideType: 'mailMerge' }, '*');
					return;
				}

        const hasNoList = email.listId === -1 || email.listId === undefined || email.isListDeleted;
				if (hasNoList) {
					Alert.error(msg.CHECK_SELECT_LISTS);
					return;
				}

				const listInfoFieldsItems = listInfoFields.map((item: any) => {
					return {
						type: 'menuitem',
						text: `${item.label}`,
						onAction: function () {
							editor.insertContent(`${item.value}`);
						}
					}
				});

				const listVarsItems = listVarsFields.map((item: any) => {
					return {
						type: 'menuitem',
						text: `${item.label}|${item.substitution}`,
						// text: `${item.label} <small>${item.substitution}</small>`,
						onAction: function () {
							editor.insertContent(`$%${item.substitution}%$`);
						}
					}
				});

				const listInfoTitle = [
					{
						type: 'menuitem',
						text: '주소록 필드'
					}
				];

				const listVarsTitle = [
					{
						type: 'menuitem',
						text: '사용자 정의 필드'
					}
				];

				const preventUseMergeItem = [
					{
						type: 'menuitem',
						text: '사용자 정의 필드를 사용하려면<br /><em>업그레이드</em> 하세요',
						onAction: function () {
							window.parent.postMessage({ msg: 'openAccountPlanModal' }, '*');
						}
					}
				]

				const items = accountPlan >= 2 ? [...listInfoTitle, ...listInfoFieldsItems, ...listVarsTitle, ...listVarsItems] : preventUseMergeItem;
				setTimeout(() => {
					callback(items);
				}, 200);
			}
		});

		editor.ui.registry.addMenuButton('stbAlign', {
			icon: 'align-left',
			text: 'stb-align',
			onSetup: function () {
				let a = Array.from(document.querySelectorAll(`#texteditor${contentId}${contentIndex} ~ .tox span`)).find(el => el.textContent === 'stb-align');
				a?.classList.add('blind');
				a?.parentElement?.classList.add('align');
			},
			onselect: function (e: any) {
				editor.execCommand(this.value());
			},
			fetch: function (callback: any) {
				setTimeout(() => {
					let menuPanel = document.querySelector('.tox-selected-menu');
					menuPanel?.classList.add('stb-align-menu');
					const currentAlign = getCurrentAlign(editor);
					let cm = currentAlign?.includes('양쪽') ? document.querySelector(`.tox-selected-menu .tox-collection__item[title="${currentAlign}"]`) : document.querySelector(`.tox-selected-menu .tox-collection__item[title="${currentAlign?.replace('정렬', '')}"]`);
					cm?.classList.add('tox-collection__item--enabled');
				}, 10);

				callback([
					{
						type: 'menuitem',
						text: 'left',
						icon: 'align-left',
						onAction: function () {
							textAlignChanged.current = true;
							editor.execCommand('JustifyLeft');
							detectSelection(editor);
						}
					},
					{
						type: 'menuitem',
						text: 'center',
						icon: 'align-center',
						onAction: function () {
							textAlignChanged.current = true;
							editor.execCommand('JustifyCenter');
							detectSelection(editor);
						}
					},
					{
						type: 'menuitem',
						text: 'right',
						icon: 'align-right',
						onAction: function () {
							textAlignChanged.current = true;
							editor.execCommand('JustifyRight');
							detectSelection(editor);
						}
					},
					{
						type: 'menuitem',
						text: 'justify',
						icon: 'align-justify',
						onAction: function () {
							textAlignChanged.current = true;
							editor.execCommand('JustifyFull');
							detectSelection(editor);
						}
					},
				]);
			},
		});
	}

	const saveTempTextToLocal = (text: string) => {
		let key = `stb_temp_textEdit_${contentId}_${contentIndex}`;
		localStorage.setItem(key, text);
	}

	const removeTempTextFromLocal = () => {
		let key = `stb_temp_textEdit_${contentId}_${contentIndex}`;
		localStorage.removeItem(key);
	}

	const extractTextAlign = (styleText: string) => {
		const regex = /text-align:\s*([^;]+);/;
		const match = styleText.match(regex);
		if (match) {
			return match[1].trim();
		}
		return null;
	}

	const getCurrentAlign = (editor: any) => {
    const currentNodeStyle = editor.selection.getNode()?.getAttribute('style');
		const textAlign = extractTextAlign(currentNodeStyle || '') || style?.textAlign;

    if (textAlign === 'left') {
      return '왼쪽';
    } else if (textAlign === 'center') {
			return '가운데';
		} else if (textAlign === 'right') {
			return '오른쪽';
		} else if (textAlign === 'justify') {
			return '양쪽정렬';
		} else {
			return null;
		}
	}

	const onChangeForeColorValue = (value: string) => {
		if (value === '') {
			const node = tinymce.selection.getNode();

			let color: string = tinymce.selection.dom.getStyle(node, 'color') || '';
			let bg: string = tinymce.selection.dom.getStyle(node, 'background-color') || '';

			setForeColorValue('');
			tinymce.execCommand('mceRemoveTextcolor', 'forecolor');
		} else {
			setForeColorValue(value);
			tinymce.execCommand('mceApplyTextcolor', 'forecolor', value);
		}
		// applyBulletStyle('forecolor', value);
	}

	const onChangeBackColorValue = (value: string) => {
		if (value === '') {
			setBackColorValue('');
			tinymce.execCommand('mceRemoveTextcolor', 'hilitecolor');
		} else {
			setBackColorValue(value);
			tinymce.execCommand('mceApplyTextcolor', 'hilitecolor', value);
		}
	}

	// const applyBulletStyle = (mode:string, value:string) => {
	// 	const node = tinymce.selection.getNode();
	// 	if (node?.parentElement?.nodeName === 'LI') {
	// 		let pLi:HTMLLIElement = node.parentElement;
	// 		pLi.style.color = value;
	// 	}
	// }

	const onEmojiClick = (event: any, emojiObject: any) => {
		tinymce.execCommand('mceInsertContent', false, emojiObject.emoji);
		toggleEmojiPicker(false);
	}

	const toggleForeColorPickerShow = (show: boolean) => {
		setForeColorPickerShow(show);
		let btn = Array.from(document.querySelectorAll('span')).find(el => el.textContent === 'stb-fore-color-picker');
		if (show) {
			btn?.parentElement?.classList.add('tox-tbtn--enabled');
		} else {
			btn?.parentElement?.classList.remove('tox-tbtn--enabled');
		}
	}

	const toggleBackColorPickerShow = (show: boolean) => {
		setBackColorPickerShow(show);
		let btn = Array.from(document.querySelectorAll('span')).find(el => el.textContent === 'stb-back-color-picker');
		if (show) {
			btn?.parentElement?.classList.add('tox-tbtn--enabled');
		} else {
			btn?.parentElement?.classList.remove('tox-tbtn--enabled');
		}
	}

	const toggleEmojiPicker = (show: boolean) => {
		setEmojiShow(show);
		let btn = Array.from(document.querySelectorAll('span')).find(el => el.textContent === 'stb-emoji-picker');
		if (show) {
			btn?.parentElement?.classList.add('tox-tbtn--enabled');
		} else {
			btn?.parentElement?.classList.remove('tox-tbtn--enabled');
		}
	}

	const fontSizeOption = () => {
		const smallFontComponents = ['permalink', 'footer'];
		if (contentCategory === undefined || smallFontComponents.includes(contentCategory)) {
			return '12px 14px 16px 18px 20px 26px';
		} else {
			return '12px 14px 16px 18px 20px 26px 32px 40px 48px 60px';
		}
	}

	const contentStyleParse = () => {
		let s = copyObject(style);
		s['paddingLeft'] = '0!important';
		s['paddingRight'] = '0!important';
		// s['lineHeight'] = boxStyle?.lineHeight; //tinyEditor 열리는 순간의 높이 결정
		s['backgroundColor'] = boxStyle?.background;

		if (textTemplate) {
			s['margin'] = '32px 60px;'
		}

		if (linkExcludes.includes(contentCategory || 'abc')) {
			return `body${stringCss(s)}`;
		} else {
			return `body${stringCss(s)} a${stringCss(commonStyle.link)} span>a {font-weight:inherit;font-style:inherit;text-decoration:inherit;}`;
		}
	}

	const handleBlur = () => {
		if (textTemplate) {
			dispatch(editContent({ target: `${contentTarget}/text`, data: editedText }));
			removeTempTextFromLocal();
		}
	};


	return (
		<div className={textTemplate ? 'text-edit' : showContent ? 'text-edit notshow' : 'text-edit'} >
			{(showToolbar) && (
				<div className={textTemplate ? "text-template" : ""}>
					<Editor
						apiKey="lkuil8ct6v9pz6arod8pmx2gix11j03f19lbz0lprnbpjn0x"
						ref={editorRef}
						onBlur={handleBlur}
						id={`texteditor${contentId}${contentIndex}`}
						initialValue={editedText}
						onEditorChange={handleEditorChange}
						init={{
							setup: function (editor: any) {
								customSetup(editor);
							},
							init_instance_callback: () => {
								setShowContent(false);
							},
							auto_focus: `texteditor${contentId}${contentIndex}`,
							skin_url: '/assets/tinymce_skin/skins/ui/stb',
							icons_url: '/assets/tinymce_icon/icons/stb/icons.js', // https://www.tiny.cloud/docs/advanced/editor-icon-identifiers/
							icons: 'stb',
							content_css: '/assets/tinymce_skin/skins/content/stb/content.css',
							content_style: `${contentStyleParse()}`,
							autoresize_bottom_margin: 0,
							autoresize_on_init: false,
							paste_data_images: false,
							menubar: false,
							branding: false,
							language: 'ko_KR',
							language_url: '/assets/i18n/tinyMCE_ko_KR.js',
							height: 0,
							plugins: [
								'advlist lists link anchor emoticons paste image autoresize autolink',
							],
							toolbar: textTemplate ? toolbar : [
								'numlist bullist mergeTagButton stbEmoji | removeformat undo redo',
								'fontselect | fontsizeselect | stbAlign | bold italic underline strikethrough | stbForeColorPicker stbBackColorPicker | link stbUnlink'
							],
							// lineheight_formats: '1 1.5 1.8 2',
							fontsize_formats: `${fontSizeOption()}`,
							font_formats: '고딕=AppleSDGothic, apple sd gothic neo, noto sans korean, noto sans korean regular, noto sans cjk kr, noto sans cjk, nanum gothic, malgun gothic, dotum, arial, helvetica, sans-serif;명조=nanum myoungjo, 바탕, batang, serif',
							// font_formats: '고딕=AppleSDGothic, noto sans, malgun gothic, dotum, sans-serif;명조=nanum myoungjo, batang, serif',
							forced_root_block: 'div',
							force_p_newlines: false,
							force_br_newlines: true,
							convert_newlines_to_brs: true,
							// remove_linebreaks : true,
							link_context_toolbar: false,
							link_title: false,
							link_assume_external_targets: false,
							target_list: false,
							formats: {
								bold: { inline: 'span', classes: 'stb-bold', styles: { fontWeight: 'bold' }, links: true },
								italic: { inline: 'span', classes: 'stb-italic', styles: { fontStyle: 'italic' }, links: true },
								underline: { inline: 'span', classes: 'stb-underline', styles: { textDecoration: 'underline' }, exact: true, links: true },
								strikethrough: { inline: 'span', classes: 'stb-line-through', styles: { textDecoration: 'line-through' }, exact: true, links: true },
								forecolor: { inline: 'span', classes: 'stb-fore-colored', styles: { color: '%value' }, links: true, remove_similar: true, clear_child_styles: true },
								hilitecolor: { inline: 'span', classes: 'stb-hilitecolor-colored', styles: { backgroundColor: '%value' }, links: true, remove_similar: true, clear_child_styles: true },
								removeformat: [
									{
										selector: 'div, span, p, b, i, em, strong, u, strike, s, font, code, blockquote, table, tr, td, small, sub, sup, pre, cite, h1, h2, h3, h4, h5, h6',
										remove: 'all',
										deep: true,
										block_expand: true,
									},
								],
							},
							contextmenu: false,
							anchor_bottom: false,
							anchor_top: false,
							valid_elements: "p[*],br[*],b[*],i[*],strong[*],em[*],i[*],h1[*],h2[*],h3[*],h4[*],h5[*],h6[*],a[*],ul[*],ol[*],li[*],span[*],div[style]",
							invalid_elements: "*[contenteditable]",
							invalid_styles: { '*': 'position top left bottom right opacity' },
							// paste_as_text: true,
							paste_preprocess: function (plugin, args) {
								let res = addLinkToText(cleanUpHtml(trimBlankString(args.content)));
								args.content = res;
							},
							paste_postprocess: function (plugin, args) {
								removeInvalidStyleAndAttr(args.node);
							},
							// paste_webkit_styles : 'color font-size font-weight font-style text-decoration background background-color text-align line-through', // 일반 웹페이지 스타일 적용
							paste_retain_style_properties: 'color font-size font-weight font-style text-decoration background background-color text-align line-through', // MS word, google doc 스타일 적용
						}}
					/>
					{
						foreColorPickerShow && (
							<div className="color-picker-in-text fore" >
								<ColorPickerPanel
									value={foreColorValue}
									position="left"
									onChangeValue={onChangeForeColorValue}
									onResetValue={() => { onChangeForeColorValue('') }}
									onClose={() => { toggleForeColorPickerShow(false) }}
									className={colorPanelFixed ? 'fix' : ''}
									leftPosition={colorPanelFixedLeft}
								/>
							</div>
						)
					}
					{
						backColorPickerShow && (
							<div className="color-picker-in-text back" >
								<ColorPickerPanel
									value={backColorValue}
									position="left"
									onChangeValue={onChangeBackColorValue}
									onResetValue={() => { onChangeBackColorValue('') }}
									onClose={() => { toggleBackColorPickerShow(false) }}
									className={colorPanelFixed ? 'fix' : ''}
									leftPosition={colorPanelFixedLeft}
								/>
							</div>
						)
					}
					{
						emojiShow && (
							<div className="color-picker-in-text emoji">
								<EmojiPickerPanel
									className={colorPanelFixed ? 'fix' : ''}
									onEmojiClick={onEmojiClick}
									onClose={() => { toggleEmojiPicker(false) }}
								/>
							</div>
						)
					}
				</div>
			)}
			{showContent && (
				<TextEditCover
					contentId={contentId}
					contentIndex={contentIndex}
					startEditing={startEditing}
					style={style}
					editedText={editedText}
					contentCategory={contentCategory || 'base'}
					commonStyle={commonStyle}
				/>
			)}
		</div>
	);
}

TextEdit.defaultProps = {
	contentField: 'text',
	contentCategory: 'base',
};

export default TextEdit;